import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { SubdomainLink } from './SubdomainLink';
import { Menu, X, User, UserPlus, Home, Search, MessageSquare, Crown, LifeBuoy, LogOut, BookOpen, ChevronDown, ChevronRight, CreditCard, History, Star, Building2, FileText, ShieldCheck, Heart, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { authService, vendorService } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import authStorage from '../utils/authStorage';

const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [displayName, setDisplayName] = useState('demo');
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, roles, logout } = useAuth();
  const isAdminPath = location.pathname.toLowerCase().startsWith('/admin');

  // Header Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [navSearchQuery, setNavSearchQuery] = useState('');
  const [searchCategories, setSearchCategories] = useState([]);
  const [searchCities, setSearchCities] = useState([]);
  const [vendorResults, setVendorResults] = useState([]);
  const [isDesktopSuggestionsOpen, setIsDesktopSuggestionsOpen] = useState(false);
  const [isMobileSuggestionsOpen, setIsMobileSuggestionsOpen] = useState(false);
  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const desktopSearchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);

  const vendorPaths = ['/my-account/dashboard', '/my-account/manage-listing', '/my-account/inquiries', '/my-account/upgrade-membership', '/my-account/reviews'];
  const isVendorActive = vendorPaths.some((p) => location.pathname.startsWith(p));
  const isMagazineActive = location.pathname.startsWith('/my-account/magazine');

  const [mobileVendorOpen, setMobileVendorOpen] = useState(() => isVendorActive);
  const [mobileMagazineOpen, setMobileMagazineOpen] = useState(() => isMagazineActive);

  useEffect(() => {
    setMobileVendorOpen(isVendorActive);
    setMobileMagazineOpen(isMagazineActive);
  }, [location.pathname, isVendorActive, isMagazineActive]);

  // Load search metadata (categories & cities) on mount
  useEffect(() => {
    const loadSearchMetadata = async () => {
      try {
        const catRes = await vendorService.getCategories(false, 'ALL');
        if (catRes.success && catRes.data) {
          setSearchCategories(catRes.data || []);
        }
        const locRes = await vendorService.getPublicLocations();
        if (locRes.success && locRes.data) {
          const allCities = [];
          const citiesMap = locRes.data.cities || {};
          for (const st in citiesMap) {
            if (Array.isArray(citiesMap[st])) {
              citiesMap[st].forEach(city => {
                if (city && !allCities.includes(city)) {
                  allCities.push(city);
                }
              });
            }
          }
          setSearchCities(allCities);
        }
      } catch (err) {
        console.error("Error loading navbar search metadata:", err);
      }
    };
    loadSearchMetadata();
  }, []);

  // Fetch live vendor suggestions debounced
  useEffect(() => {
    const q = navSearchQuery.trim();
    if (!q) {
      setVendorResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await vendorService.getPublicListings({ q }, 0, 5);
        if (res.success && res.data) {
          const list = res.data.listings || (Array.isArray(res.data) ? res.data : []);
          setVendorResults(list);
        }
      } catch (err) {
        // Silently handle
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [navSearchQuery]);

  // Click outside to close suggestion dropdowns
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (desktopSearchRef.current && !desktopSearchRef.current.contains(e.target)) {
        setIsDesktopSuggestionsOpen(false);
      }
      if (mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setIsMobileSuggestionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getGlobalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem('search_pref_global') || '{}');
      return data[String(key).toLowerCase()] || 0;
    } catch {
      return 0;
    }
  };

  const incrementGlobalSearchScore = (key) => {
    try {
      const data = JSON.parse(localStorage.getItem('search_pref_global') || '{}');
      const k = String(key).toLowerCase();
      data[k] = (data[k] || 0) + 1;
      localStorage.setItem('search_pref_global', JSON.stringify(data));
    } catch {}
  };

  const getMatchTier = (label, q) => {
    if (!label || !q) return 99;
    const clean = label.toLowerCase();
    const queryLower = q.toLowerCase();

    // Tier 0: Exact match
    if (clean === queryLower) return 0;

    // Tier 1: Starts with query (Prefix match e.g., 'Bhuj' starts with 'b')
    if (clean.startsWith(queryLower)) return 1;

    // Tier 2: Word boundary starts with query (e.g. 'South Bangalore' -> 'b')
    const words = clean.split(/[\s\-_/,]+/);
    if (words.some(w => w.startsWith(queryLower))) return 2;

    // Tier 3: Substring contains
    if (clean.includes(queryLower)) return 3;

    return 99;
  };

  // Compute suggestions prioritized by Match Tier & Popularity
  const navSuggestions = useMemo(() => {
    const q = navSearchQuery.trim().toLowerCase();
    const results = [];

    if (!q) {
      // Empty search / initial focus: Top popular Categories & Cities
      const topCats = [...searchCategories]
        .sort((a, b) => getGlobalSearchScore(b.name) - getGlobalSearchScore(a.name))
        .slice(0, 3)
        .map(cat => ({
          id: cat.id,
          title: cat.name,
          type: 'category',
          section: (cat.section || 'VENDOR').toUpperCase(),
          score: getGlobalSearchScore(cat.name)
        }));

      const topCities = [...searchCities]
        .sort((a, b) => getGlobalSearchScore(b) - getGlobalSearchScore(a))
        .slice(0, 3)
        .map(city => ({
          id: city,
          title: city,
          type: 'city',
          score: getGlobalSearchScore(city)
        }));

      return [...topCats, ...topCities].slice(0, 6);
    }

    // Match Categories
    searchCategories.forEach(cat => {
      const tier = getMatchTier(cat.name, q);
      if (tier < 99) {
        results.push({
          id: cat.id,
          title: cat.name,
          type: 'category',
          section: (cat.section || 'VENDOR').toUpperCase(),
          tier,
          score: getGlobalSearchScore(cat.name)
        });
      }
    });

    // Match Cities
    searchCities.forEach(city => {
      const tier = getMatchTier(city, q);
      if (tier < 99) {
        results.push({
          id: city,
          title: city,
          type: 'city',
          tier,
          score: getGlobalSearchScore(city)
        });
      }
    });

    // Match Vendors / Listings
    vendorResults.forEach(v => {
      const name = v.companyName || v.listingName || v.name || '';
      const tier = getMatchTier(name, q);
      if (tier < 99 && name) {
        let vSec = v.categorySection || v.category?.section || v.section;
        if (!vSec && v.categoryId && searchCategories.length > 0) {
          const matchedCat = searchCategories.find(c => String(c.id) === String(v.categoryId));
          if (matchedCat) {
            vSec = matchedCat.section;
          }
        }
        vSec = (vSec || 'VENDOR').toUpperCase();

        results.push({
          id: v.id,
          title: name,
          subtitle: v.city ? `${v.city}${v.state ? `, ${v.state}` : ''}` : '',
          type: 'vendor',
          section: vSec,
          tier,
          score: getGlobalSearchScore(name) + 5
        });
      }
    });

    // Sort results: Tier -> Popularity Score -> Length -> Alphabetical
    results.sort((a, b) => {
      if (a.tier !== b.tier) return a.tier - b.tier;
      if (b.score !== a.score) return b.score - a.score;
      if (a.title.length !== b.title.length) return a.title.length - b.title.length;
      return a.title.localeCompare(b.title);
    });

    // Deduplicate and cap at 6
    const seen = new Set();
    const unique = [];
    for (const r of results) {
      const key = `${r.type}_${r.title.toLowerCase()}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(r);
      }
      if (unique.length >= 6) break;
    }

    return unique;
  }, [navSearchQuery, searchCategories, searchCities, vendorResults]);

  const handleSelectSuggestion = (item) => {
    incrementGlobalSearchScore(item.title);
    setIsDesktopSuggestionsOpen(false);
    setIsMobileSuggestionsOpen(false);
    setNavSearchQuery(item.title);
    setIsSearchOpen(true);

    const isCurrentlyInDesignerSection = location.pathname.startsWith('/interior-designers-and-architects') || location.pathname.startsWith('/interior-designers-architects');

    if (item.type === 'category') {
      if (item.section === 'DESIGNER_ARCHITECT') {
        navigate(`/interior-designers-and-architects?category=${encodeURIComponent(item.title)}`);
      } else {
        navigate(`/listing?category=${encodeURIComponent(item.title)}`);
      }
    } else if (item.type === 'city') {
      if (isCurrentlyInDesignerSection) {
        navigate(`/interior-designers-and-architects?city=${encodeURIComponent(item.title)}`);
      } else {
        navigate(`/listing?city=${encodeURIComponent(item.title)}`);
      }
    } else {
      const slug = slugify(item.title || item.companyName);
      navigate(`/listing/${slug || item.id}`);
    }
  };

  // Sync navSearchQuery and isSearchOpen with URL query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    if (q) {
      setNavSearchQuery(q);
      setIsSearchOpen(true);
    } else {
      // Automatically close top search bar when URL has no q query (e.g. when bottom dropdown filters are applied)
      setIsSearchOpen(false);
      setNavSearchQuery('');
    }
  }, [location.search, location.pathname]);

  const handleOpenSearch = () => {
    setIsSearchOpen(true);
    setIsDesktopSuggestionsOpen(true);
    setIsMobileSuggestionsOpen(true);
    setTimeout(() => {
      if (desktopSearchInputRef.current) {
        desktopSearchInputRef.current.focus();
        desktopSearchInputRef.current.select();
      }
      if (mobileSearchInputRef.current) {
        mobileSearchInputRef.current.focus();
        mobileSearchInputRef.current.select();
      }
    }, 50);
  };

  // Auto-focus and select search input on opening
  useEffect(() => {
    if (isSearchOpen) {
      setIsDesktopSuggestionsOpen(true);
      setIsMobileSuggestionsOpen(true);
      const timer1 = setTimeout(() => {
        if (desktopSearchInputRef.current) {
          desktopSearchInputRef.current.focus();
          desktopSearchInputRef.current.select();
        }
        if (mobileSearchInputRef.current) {
          mobileSearchInputRef.current.focus();
          mobileSearchInputRef.current.select();
        }
      }, 50);
      const timer2 = setTimeout(() => {
        if (desktopSearchInputRef.current) {
          desktopSearchInputRef.current.focus();
          desktopSearchInputRef.current.select();
        }
        if (mobileSearchInputRef.current) {
          mobileSearchInputRef.current.focus();
          mobileSearchInputRef.current.select();
        }
      }, 150);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isSearchOpen]);

  // Close search on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isSearchOpen) {
        handleCloseSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isSearchOpen, location.search, location.pathname]);

  const handleNavSearchSubmit = (e) => {
    if (e) e.preventDefault();
    const queryTrimmed = navSearchQuery.trim();
    if (!queryTrimmed) return;

    setIsDesktopSuggestionsOpen(false);
    setIsMobileSuggestionsOpen(false);

    const isCurrentlyInDesignerSection = location.pathname.startsWith('/interior-designers-and-architects') || location.pathname.startsWith('/interior-designers-architects');

    // Check if entered query matches a known category
    const queryLower = queryTrimmed.toLowerCase();
    const matchedCategory = searchCategories.find(c => c.name.toLowerCase() === queryLower);

    if (matchedCategory) {
      const isDesigner = (matchedCategory.section || 'VENDOR').toUpperCase() === 'DESIGNER_ARCHITECT';
      if (isDesigner) {
        navigate(`/interior-designers-and-architects?category=${encodeURIComponent(matchedCategory.name)}`);
      } else {
        navigate(`/listing?category=${encodeURIComponent(matchedCategory.name)}`);
      }
    } else if (isCurrentlyInDesignerSection) {
      navigate(`/interior-designers-and-architects?q=${encodeURIComponent(queryTrimmed)}`);
    } else {
      navigate(`/listing?q=${encodeURIComponent(queryTrimmed)}`);
    }
    setIsSearchOpen(true); // Keep search bar open showing current search term
  };

  const handleCloseSearch = () => {
    setIsSearchOpen(false);
    setIsDesktopSuggestionsOpen(false);
    setIsMobileSuggestionsOpen(false);
    setNavSearchQuery('');

    // If a search query filter is currently applied in the URL, remove it and refresh listings
    const params = new URLSearchParams(location.search);
    if (params.has('q')) {
      params.delete('q');
      params.delete('page');
      const newQuery = params.toString();
      navigate(`${location.pathname}${newQuery ? `?${newQuery}` : ''}`);
    }
  };

  // Vendor Profile Photo URL State & Helper Function
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const getAdminRoleName = () => {
    if (!roles) return 'Admin Console';
    if (roles.includes('ROLE_SUPER_ADMIN')) return 'Super Admin Console';
    if (roles.includes('ROLE_ADMIN')) return 'Admin Console';
    if (roles.includes('ROLE_SALES_ADMIN')) return 'Sales Console';
    if (roles.includes('ROLE_LISTING_ADMIN')) return 'Listing Console';
    if (roles.includes('ROLE_SUPPORT_ADMIN')) return 'Support Console';
    if (roles.includes('ROLE_MAGAZINE_SALES')) return 'Magazine Sales Console';
    return 'Admin Console';
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const updateUserProfileData = async () => {
      const storedUser = authStorage.getItem('user');
      if (!storedUser) return;
      
      try {
        const userObj = JSON.parse(storedUser);
        const initialName = userObj.name || (userObj.emailId || userObj.username || 'demo');
        setDisplayName(initialName);
        setProfilePhotoUrl(userObj.photoUrl || '');

        const email = userObj.emailId || userObj.username;
        if (email && email.includes('@')) {
          const res = await authService.getUserProfile(email);
          if (res.success && res.data) {
            const profile = res.data;
            if (profile.name) {
              setDisplayName(profile.name);
            }
            if (profile.photoUrl) {
              setProfilePhotoUrl(profile.photoUrl);
            }
            authStorage.setItem('user', JSON.stringify({
              ...userObj,
              id: profile.id,
              name: profile.name,
              emailId: profile.emailId,
              photoUrl: profile.photoUrl || ''
            }));
          }
        }
      } catch (err) {
        console.error("Error setting Navbar profile data:", err);
      }
    };

    updateUserProfileData();
    window.addEventListener('storage', updateUserProfileData);
    window.addEventListener('profilePhotoChanged', updateUserProfileData);
    return () => {
      window.removeEventListener('storage', updateUserProfileData);
      window.removeEventListener('profilePhotoChanged', updateUserProfileData);
    };
  }, [isAdmin]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Vendors', path: '/listing' },
    { name: 'Architects/Designers', path: '/interior-designers-and-architects' },
  ];

  return (
    <nav className={`${
      isAdminPath 
        ? `fixed ${scrolled ? 'bg-[#23977a] shadow-md' : 'bg-transparent'} border-transparent` 
        : `absolute bg-white border-b border-gray-100 ${scrolled ? 'shadow-md' : ''}`
    } top-0 left-0 right-0 w-full z-[100] transition-all duration-300 flex items-center h-16 sm:h-20`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 w-full">
        <div className="flex justify-between items-center h-full">
          {/* Main Logo from public folder */}
          <SubdomainLink to={isAdmin() ? "/admin" : "/"} className="flex items-center shrink-0 mr-2 sm:mr-4">
            <img
              src="/Top_Interior_India_1.png"
              alt="Top Interiors India"
              className={`h-9 min-[380px]:h-10 sm:h-11 md:h-12 w-auto object-contain transition-all ${isAdminPath ? 'brightness-0 invert' : ''}`}
            />
          </SubdomainLink>

          {/* Center Links */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-10 relative">
            {!isAdmin() && navLinks.map((link) => (
              <SubdomainLink
                key={link.name}
                to={link.path}
                className={`text-[15px] xl:text-[16px] font-medium transition-colors hover:text-primary whitespace-nowrap ${location.pathname === link.path ? 'text-primary' : 'text-secondary'}`}
              >
                {link.name}
              </SubdomainLink>
            ))}
          </div>

          {/* Right Auth Links & Search */}
          <div className="hidden lg:flex items-center gap-2.5">
            <AnimatePresence mode="wait">
              {isSearchOpen ? (
                <motion.form
                  key="desktop-search-open"
                  ref={desktopSearchRef}
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 360 }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleNavSearchSubmit}
                  className="relative flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 shadow-xs ml-4"
                >
                  <Search size={16} className="text-[#2ab391] shrink-0 mr-2" />
                  <input
                    ref={desktopSearchInputRef}
                    autoFocus
                    type="text"
                    value={navSearchQuery}
                    onChange={(e) => {
                      setNavSearchQuery(e.target.value);
                      setIsDesktopSuggestionsOpen(true);
                    }}
                    onFocus={() => setIsDesktopSuggestionsOpen(true)}
                    placeholder="Search listings, category, city..."
                    className="w-full bg-transparent text-sm text-gray-800 placeholder:text-gray-400 placeholder:font-normal focus:outline-none font-normal"
                  />
                  <button
                    type="button"
                    onClick={handleCloseSearch}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-full cursor-pointer focus:outline-none shrink-0"
                    title="Close search"
                  >
                    <X size={15} />
                  </button>

                  {/* Desktop Suggestions Dropdown */}
                  {isDesktopSuggestionsOpen && isSearchOpen && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-150 rounded-xl shadow-xl z-[150] overflow-hidden text-left divide-y divide-gray-100 animate-in fade-in zoom-in-95 duration-100">
                      {navSuggestions.length > 0 ? (
                        navSuggestions.map((item, idx) => (
                          <div
                            key={`desk_${item.type}_${item.id}_${idx}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectSuggestion(item);
                            }}
                            className="px-4 py-2.5 cursor-pointer hover:bg-gray-50/90 transition-colors flex items-center justify-between gap-3 group"
                          >
                            <div className="flex flex-col min-w-0 text-left flex-1">
                              <span className="font-semibold text-gray-900 group-hover:text-[#2ab391] text-[14px] leading-snug truncate transition-colors">
                                {item.title}
                              </span>
                              <span className="text-[12px] text-gray-400 capitalize font-normal leading-tight mt-0.5 truncate">
                                {item.type === 'vendor'
                                  ? (item.section === 'DESIGNER_ARCHITECT'
                                      ? (item.subtitle ? `Architect/Designer • ${item.subtitle}` : 'Architect/Designer')
                                      : (item.subtitle ? `Vendor • ${item.subtitle}` : 'Vendor'))
                                  : item.type === 'category'
                                  ? (item.section === 'DESIGNER_ARCHITECT' ? 'Architects/Designers' : 'Vendors Category')
                                  : 'City'}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-4 py-3 text-xs text-gray-400 italic text-center">
                          No matching suggestions
                        </div>
                      )}
                    </div>
                  )}
                </motion.form>
              ) : (
                <motion.div
                  key="desktop-search-closed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-2.5"
                >
                  {!isAdminPath && (
                    <button
                      type="button"
                      onClick={handleOpenSearch}
                      className="flex items-center gap-2 text-xs sm:text-sm font-normal text-gray-400 hover:text-[#2ab391] bg-gray-50/80 hover:bg-teal-50/60 border border-gray-200 hover:border-[#2ab391]/30 px-3.5 py-1.5 rounded-full transition-all duration-150 cursor-pointer shadow-2xs group shrink-0"
                      title="Search Listings"
                      aria-label="Search Listings"
                    >
                      <Search size={14} className="text-[#2ab391] transition-transform group-hover:scale-110 shrink-0" />
                      <span className="text-gray-400 group-hover:text-[#2ab391] font-normal">Search Listings</span>
                    </button>
                  )}

                  {authStorage.getItem('token') ? (
                    isAdmin() ? (
                      <div className="flex items-center gap-2">
                        <SubdomainLink 
                          to="/admin" 
                          className={`flex items-center gap-1.5 text-xs sm:text-sm font-bold transition-all px-3.5 py-1.5 rounded-full shadow-2xs hover:shadow-xs active:scale-98 shrink-0 ${
                            isAdminPath 
                              ? 'text-white bg-white/15 hover:bg-white/25 border border-white/25 backdrop-blur-sm' 
                              : 'text-white bg-[#2ab391] hover:bg-[#23977a]'
                          }`}
                        >
                          <ShieldCheck size={14} className="shrink-0" />
                          <span>{getAdminRoleName()}</span>
                        </SubdomainLink>
                        <button 
                          onClick={logout}
                          className={`flex items-center gap-1.5 text-xs sm:text-sm font-semibold transition-all px-3 py-1.5 rounded-full active:scale-98 cursor-pointer shadow-2xs shrink-0 ${
                            isAdminPath 
                              ? 'text-white bg-white/10 hover:bg-red-500/80 border border-white/20 hover:border-red-400 backdrop-blur-sm' 
                              : 'text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100/80 border border-red-200/70'
                          }`}
                        >
                          <LogOut size={13} className="shrink-0" />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center">
                        <SubdomainLink 
                          to="/my-account/profile" 
                          className="flex items-center gap-2 bg-white hover:bg-teal-50/40 border border-gray-200 hover:border-[#2ab391]/40 rounded-full pl-1.5 pr-3.5 py-1 transition-all shadow-2xs hover:shadow-xs group shrink-0 cursor-pointer"
                        >
                          {profilePhotoUrl ? (
                            <img src={getImageUrl(profilePhotoUrl)} alt="Profile" className="w-7 h-7 rounded-full object-cover border border-teal-100/80 shrink-0" />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-teal-50 border border-teal-100 text-[#2ab391] flex items-center justify-center font-bold text-xs shrink-0 group-hover:bg-[#2ab391] group-hover:text-white transition-colors">
                              <User size={13} />
                            </div>
                          )}
                          <span className="text-xs sm:text-sm font-semibold text-gray-700 group-hover:text-[#2ab391] transition-colors max-w-[130px] truncate">
                            {displayName}
                          </span>
                        </SubdomainLink>
                      </div>
                    )
                  ) : (
                    <div className="flex items-center gap-3 pl-1.5">
                      <SubdomainLink 
                        to="/sign-up" 
                        className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-[#2ab391] transition-colors"
                      >
                        Sign Up
                      </SubdomainLink>
                      <span className="text-gray-300 select-none">|</span>
                      <SubdomainLink 
                        to="/login" 
                        className="text-xs sm:text-sm font-semibold text-gray-700 hover:text-[#2ab391] transition-colors"
                      >
                        Login
                      </SubdomainLink>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Mobile Menu Button & Guest Auth Links / Search */}
          <div className="lg:hidden flex items-center gap-1 sm:gap-2 flex-1 justify-end min-w-0">
            <AnimatePresence mode="wait">
              {isSearchOpen ? (
                <motion.form
                  key="mob-search-open"
                  ref={mobileSearchRef}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleNavSearchSubmit}
                  className="flex-1 min-w-0 max-w-[270px] sm:max-w-[340px] ml-4 sm:ml-6 mr-1 sm:mr-2 relative flex items-center bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5 shadow-xs"
                >
                  <Search size={15} className="text-[#2ab391] shrink-0 mr-1.5" />
                  <input
                    ref={mobileSearchInputRef}
                    autoFocus
                    type="text"
                    value={navSearchQuery}
                    onChange={(e) => {
                      setNavSearchQuery(e.target.value);
                      setIsMobileSuggestionsOpen(true);
                    }}
                    onFocus={() => setIsMobileSuggestionsOpen(true)}
                    placeholder="Search listings..."
                    className="w-full bg-transparent text-xs text-gray-800 placeholder:text-gray-400 placeholder:font-normal focus:outline-none font-normal"
                  />
                  <button
                    type="button"
                    onClick={handleCloseSearch}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded-full cursor-pointer focus:outline-none shrink-0"
                    title="Close search"
                  >
                    <X size={14} />
                  </button>

                  {/* Mobile Suggestions Dropdown */}
                  {isMobileSuggestionsOpen && isSearchOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1.5 bg-white border border-gray-150 rounded-xl shadow-xl z-[150] overflow-hidden text-left divide-y divide-gray-100 animate-in fade-in zoom-in-95 duration-100">
                      {navSuggestions.length > 0 ? (
                        navSuggestions.map((item, idx) => (
                          <div
                            key={`mob_${item.type}_${item.id}_${idx}`}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleSelectSuggestion(item);
                            }}
                            className="px-3.5 py-2 cursor-pointer hover:bg-gray-50/90 transition-colors flex items-center justify-between gap-2 group"
                          >
                            <div className="flex flex-col min-w-0 text-left flex-1">
                              <span className="font-semibold text-gray-900 group-hover:text-[#2ab391] text-[13px] leading-snug truncate transition-colors">
                                {item.title}
                              </span>
                              <span className="text-[11px] text-gray-400 capitalize font-normal leading-tight mt-0.5 truncate">
                                {item.type === 'vendor'
                                  ? (item.section === 'DESIGNER_ARCHITECT'
                                      ? (item.subtitle ? `Architect/Designer • ${item.subtitle}` : 'Architect/Designer')
                                      : (item.subtitle ? `Vendor • ${item.subtitle}` : 'Vendor'))
                                  : item.type === 'category'
                                  ? (item.section === 'DESIGNER_ARCHITECT' ? 'Architects/Designers' : 'Vendors Category')
                                  : 'City'}
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2.5 text-xs text-gray-400 italic text-center">
                          No matching suggestions
                        </div>
                      )}
                    </div>
                  )}
                </motion.form>
              ) : (
                <motion.div
                  key="mob-search-closed"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="flex items-center gap-1 sm:gap-2"
                >
                  {!isAdminPath && (
                    <button
                      type="button"
                      onClick={handleOpenSearch}
                      className="p-1.5 text-[#2ab391] hover:text-[#23977a] rounded-lg transition-colors cursor-pointer"
                      title="Search"
                      aria-label="Search"
                    >
                      <Search size={20} />
                    </button>
                  )}

                  {!authStorage.getItem('token') && (
                    <div className="flex items-center gap-1.5 mr-0.5 sm:mr-1">
                      <SubdomainLink
                        to="/login"
                        className="text-xs font-semibold text-primary border border-primary/20 hover:bg-primary/5 transition-all py-1.5 px-2.5 sm:px-3 rounded-lg bg-white"
                      >
                        Login
                      </SubdomainLink>
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              onClick={() => setIsOpen(!isOpen)} 
              className={`p-2 rounded-lg transition-colors shrink-0 ${
                isAdminPath 
                  ? 'text-white hover:bg-white/10' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
              aria-label="Toggle menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-xs lg:hidden"
            />

            {/* Sliding Drawer */}
            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 220 }}
              className="fixed top-0 left-0 bottom-0 z-[120] w-[300px] max-w-[85vw] bg-white shadow-xl flex flex-col lg:hidden border-r border-gray-200/80 text-left"
            >
              {authStorage.getItem('token') && !isAdmin() ? (
                /* Profile Header Card for Logged-In Vendors */
                <div className="p-4 border-b border-gray-100 bg-slate-50/60 relative">
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="absolute top-3.5 right-3.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 p-1.5 rounded-full transition-colors"
                    aria-label="Close menu"
                  >
                    <X size={18} />
                  </button>
                  
                  <div className="flex items-center gap-3 pr-8">
                    {/* Avatar */}
                    <SubdomainLink 
                      to="/my-account/profile"
                      onClick={() => setIsOpen(false)}
                      className="w-11 h-11 bg-teal-50 text-[#2ab391] rounded-full border border-teal-100 flex items-center justify-center overflow-hidden flex-shrink-0 shadow-2xs hover:opacity-90 transition-opacity"
                    >
                      {profilePhotoUrl ? (
                        <img src={getImageUrl(profilePhotoUrl)} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User size={22} className="text-[#2ab391]" />
                      )}
                    </SubdomainLink>

                    {/* Name & My Profile Action */}
                    <div className="flex flex-col min-w-0 flex-1 justify-center">
                      <SubdomainLink
                        to="/my-account/profile"
                        onClick={() => setIsOpen(false)}
                        className="font-semibold text-sm text-gray-900 truncate leading-snug hover:text-[#2ab391] transition-colors"
                      >
                        {displayName}
                      </SubdomainLink>
                      <SubdomainLink 
                        to="/my-account/profile" 
                        onClick={() => setIsOpen(false)} 
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#2ab391] hover:text-[#1e876e] transition-colors mt-0.5 w-fit"
                      >
                        <User size={12} />
                        <span>My Profile</span>
                      </SubdomainLink>
                    </div>
                  </div>
                </div>
              ) : (
                /* Standard Minimal Header for Admin/Guests */
                <div className="flex items-center justify-end px-5 py-4 border-b border-gray-100 bg-slate-50/60">
                  <button 
                    onClick={() => setIsOpen(false)} 
                    className="text-gray-400 hover:text-gray-700 hover:bg-gray-200/60 p-1.5 rounded-full transition-colors"
                  >
                    <X size={18} />
                  </button>
                </div>
              )}

              {/* Menu Navigation Content */}
              <div className="flex-1 overflow-y-auto p-3 space-y-0.5">
                {/* General Explore Section Header */}
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Explore
                </div>

                {/* Explore Links */}
                {[
                  { name: 'Home', path: '/', icon: <Home size={17} /> },
                  { name: 'Vendors', path: '/listing', icon: <Search size={17} /> },
                  { name: 'Architects/Designers', path: '/interior-designers-and-architects', icon: <Building2 size={17} /> }
                ].map(link => (
                  <SubdomainLink
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === link.path 
                        ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                      {link.icon}
                    </span>
                    <span>{link.name}</span>
                  </SubdomainLink>
                ))}

                {/* Guest Account Section in Mobile Menu */}
                {!authStorage.getItem('token') && (
                  <div className="border-t border-gray-100 mt-4 pt-3 space-y-0.5">
                    <div className="px-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      Account
                    </div>
                    {[
                      { name: 'Sign Up', path: '/sign-up', icon: <UserPlus size={17} /> },
                      { name: 'Login', path: '/login', icon: <User size={17} /> }
                    ].map(link => (
                      <SubdomainLink
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          location.pathname === link.path 
                            ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                      >
                        <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                          {link.icon}
                        </span>
                        <span>{link.name}</span>
                      </SubdomainLink>
                    ))}
                  </div>
                )}

                {/* Account Section Header */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div className="px-3 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                    Account
                  </div>
                )}

                {/* ── Vendor Collapsible Menu ─────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div>
                    <button
                      onClick={() => setMobileVendorOpen(!mobileVendorOpen)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        mobileVendorOpen ? 'text-gray-900 bg-gray-50' : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <Building2 size={17} className="text-gray-500" />
                        <span>Vendor</span>
                      </span>
                      {mobileVendorOpen ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
                    </button>

                    {mobileVendorOpen && (
                      <div className="space-y-0.5 mt-0.5">
                        {[
                          { name: 'Dashboard', path: '/my-account/dashboard', icon: <LayoutDashboard size={16} /> },
                          { name: 'Vendor Listing', path: '/my-account/manage-listing', icon: <FileText size={16} /> },
                          { name: 'Inquiry', path: '/my-account/inquiries', icon: <MessageSquare size={16} /> },
                          { name: 'Upgrade Membership', path: '/my-account/upgrade-membership', icon: <Crown size={16} /> },
                          { name: 'Reviews', path: '/my-account/reviews', icon: <Star size={16} /> },
                        ].map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-3 pl-7 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              location.pathname === link.path 
                                ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                              {link.icon}
                            </span>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Magazine Collapsible Menu ────────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <div>
                    <button
                      onClick={() => setMobileMagazineOpen(!mobileMagazineOpen)}
                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        mobileMagazineOpen ? 'text-gray-900 bg-gray-50' : 'text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <BookOpen size={17} className="text-gray-500" />
                        <span>Magazine</span>
                      </span>
                      {mobileMagazineOpen ? <ChevronDown size={15} className="text-gray-400" /> : <ChevronRight size={15} className="text-gray-400" />}
                    </button>

                    {mobileMagazineOpen && (
                      <div className="space-y-0.5 mt-0.5">
                        {[
                          { name: 'Your Subscription', path: '/my-account/magazine/subscription', icon: <CreditCard size={16} /> },
                          { name: 'Subscribe', path: '/my-account/magazine/subscribe', icon: <BookOpen size={16} /> },
                          { name: 'History of Payment', path: '/my-account/magazine/history', icon: <History size={16} /> },
                        ].map(link => (
                          <Link
                            key={link.name}
                            to={link.path}
                            onClick={() => setIsOpen(false)}
                            className={`flex items-center gap-3 pl-7 pr-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              location.pathname === link.path 
                                ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                          >
                            <span className={location.pathname === link.path ? 'text-[#2ab391]' : 'text-gray-400'}>
                              {link.icon}
                            </span>
                            <span>{link.name}</span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* ── My Favorites Top-level Menu ─────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <Link
                    to="/my-account/favorites"
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === '/my-account/favorites' 
                        ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <Heart size={17} className={location.pathname === '/my-account/favorites' ? 'text-[#2ab391]' : 'text-gray-400'} />
                    <span>My Favorites</span>
                  </Link>
                )}

                {/* ── Help & Support Top-level Menu ─────────────────── */}
                {authStorage.getItem('token') && !isAdmin() && (
                  <Link
                    to="/my-account/support-tickets"
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname === '/my-account/support-tickets' 
                        ? 'bg-teal-50 text-[#2ab391] font-semibold' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    <LifeBuoy size={17} className={location.pathname === '/my-account/support-tickets' ? 'text-[#2ab391]' : 'text-gray-400'} />
                    <span>Help & Support</span>
                  </Link>
                )}

                {/* Auth Actions or Admin Control */}
                {authStorage.getItem('token') && (
                  <div className="border-t border-gray-100 mt-3 pt-3">
                    {isAdmin() ? (
                      <div className="space-y-0.5">
                        <div className="px-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                          Administration
                        </div>
                        <SubdomainLink
                          to="/admin"
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-[#2ab391] hover:bg-gray-100"
                        >
                          <User size={17} />
                          <span>{getAdminRoleName()}</span>
                        </SubdomainLink>
                        <button
                          onClick={() => {
                            setIsOpen(false);
                            logout();
                          }}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                        >
                          <LogOut size={17} />
                          <span>Sign Out</span>
                        </button>
                      </div>
                    ) : (
                      /* Vendor Log Out */
                      <button
                        onClick={() => {
                          setIsOpen(false);
                          logout();
                        }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 w-full text-left transition-colors"
                      >
                        <LogOut size={17} />
                        <span>Sign Out</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
